import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';

import { HubConnection, HubConnectionBuilder, LogLevel } from '@microsoft/signalr';

import { ContractSignatureSessionResolved } from '@models/index';

import { environment } from '@environments/environment';

// Aviso de que la sesion de firma se ha resuelto (completa o rechazada). No es fuente de la verdad: el
// backend lo emite una sola vez y no lo reenvia, asi que si el socket estaba caido en ese momento se
// pierde; quien escucha tiene que volver a consultar el estado al reconectar. El backend empuja a todas
// las conexiones vivas del socio, asi que el `connectionId` no se manda a ningun sitio.
//
// Sin `providedIn`: cada modal de firma abre y cierra su propia conexion.
@Injectable()
export class ContractSignatureSessionSignalRService {
    private readonly resolvedEvent = 'ContractSignatureSessionResolved';
    private connection: HubConnection | null = null;

    start(userToken: string): Promise<void> {
        this.connection = new HubConnectionBuilder()
            .withUrl(`${environment.apps.realtime}/${environment.hubs.contractSignatureSession}`, {
                accessTokenFactory: () => userToken
            })
            .withAutomaticReconnect()
            .configureLogging(LogLevel.Warning)
            .build();
        // El hub saluda con su `connectionId` al conectar; no se usa, pero sin handler SignalR lo avisa en consola.
        this.connection.on('onConnectedAsync', () => undefined);

        return this.connection.start();
    }

    onResolved(): Observable<ContractSignatureSessionResolved> {
        return new Observable<ContractSignatureSessionResolved>((observer) => {
            const handler = (payload: string) => observer.next(new ContractSignatureSessionResolved().build(payload));

            this.connection?.on(this.resolvedEvent, handler);

            return () => this.connection?.off(this.resolvedEvent, handler);
        });
    }

    // SignalR no permite quitar un callback de `onreconnected`: se ignora cuando ya nadie escucha.
    onReconnected(): Observable<void> {
        return new Observable<void>((observer) => {
            let isListening = true;

            this.connection?.onreconnected(() => {
                if (isListening) {
                    observer.next();
                }
            });

            return () => (isListening = false);
        });
    }

    stop(): Promise<void> {
        return this.connection?.stop() ?? Promise.resolve();
    }
}
